import api from 'api';
import { wait } from '@hapi/hoek';
import qApp from 'websockets/utils/qdtCapabilityApp';
import coeusConfig from 'coeus.config';
import store from 'store';

const enigma = require('enigma.js/enigma');
const schema = require('enigma.js/schemas/12.170.2.json');
const SenseUtilities = require('enigma.js/sense-utilities');
const publicAccess = coeusConfig?.feature?.public_access?.enabled;

const MAX_RETRIES = 5;
const DEBUG = false;

const SocketState = {
    IDLE: 'idle',
    INITIALISING: 'initialising',
    OPENING_SESSION: 'opening',
    CLOSING_SESSION: 'closing',
    RESUMING_SESSION: 'resuming',
    SUSPENDING_SESSION: 'suspending',
    OPENED: 'opened',
    SUSPENDED: 'suspended',
};

export const retryOperation = (operation, delay, retries) =>
    new Promise((resolve, reject) => {
        return operation()
            .then(resolve)
            .catch((reason) => {
                if (retries > 0) {
                    return wait(delay)
                        .then(
                            retryOperation.bind(
                                null,
                                operation,
                                delay,
                                retries - 1
                            )
                        )
                        .then(resolve)
                        .catch(reject);
                }
                return reject(reason);
            });
    });

class QlikWebSocketManager {
    constructor() {
        this.session = null;
        this.globalInterface = null;
        this.isOpen = false;
        this.suspended = false;
        this.qAppPromise = null;
        this.config = null;
        this.qTicket = null;
        this.state = SocketState.IDLE;
        this.retries = 0;
        this.loggedIn = false;
    }

    async initSocket(config) {
        this.state = SocketState.INITIALISING;
        this.config = config;

        if (this.config.isAuthenticated || !publicAccess) {
            if (!this.loggedIn) {
                await this.login();
            }
        }

        await this._initQApp();
    }

    async login() {
        const servernameprefix = `https://${this.config.host}/${this.config.prefix}`;
        return new Promise((resolve, reject) => {
            api.auth
                .qlik()
                .then((headers) => {
                    const xrfkey = headers['X-Qlik-xrfkey'];
                    return fetch(
                        `${servernameprefix}/qrs/about?xrfkey=${xrfkey}`,
                        {
                            method: 'GET',
                            mode: 'cors',
                            credentials: 'include',
                            headers,
                        }
                    ).then((response) => {
                        this.loggedIn = response.status === 200;
                        resolve();
                    });
                })
                .catch((err) => {
                    reject();
                    console.error(err);
                });
        });
    }

    canRetry() {
        return this.retries < MAX_RETRIES;
    }

    async _initQApp() {
        console.info('Loading qApp...');
        if (this.config) {
            this.qAppPromise = await qApp(this.config);
        } else {
            throw new Error('no config available');
        }
    }

    async setupQApp() {
        return await retryOperation(
            async () => await qApp(this.config, this.qTicket),
            500,
            MAX_RETRIES
        );
    }

    async _getQTicket() {
        let qTicket = null;

        if (!this.config.isAuthenticated) {
            return qTicket;
        }

        try {
            const response = await api.users.qlikLoginSSO();
            qTicket = response.qTicket;
        } catch (err) {
            throw new Error(`Error logging into qlik: ${err.message}`);
        }
        return qTicket;
    }

    _getSocketUrl() {
        return SenseUtilities.buildUrl(this.config);
    }

    _getResponseInterceptors() {
        return [
            {
                // We only want to handle failed responses from QIX Engine:
                onRejected: function retryAbortedError(
                    sessionReference,
                    request,
                    error
                ) {
                    console.info('Websocket error', error);

                    // We only want to handle aborted QIX errors:
                    if (
                        error.code ===
                        schema.enums.LocalizedErrorCode.LOCERR_GENERIC_ABORTED
                    ) {
                        // We keep track of how many consecutive times we have tried to do this call:
                        request.tries = (request.tries || 0) + 1;
                        // We do not want to get stuck in an infinite loop here if something has gone
                        // awry, so we only retry until we have reached MAX_RETRIES:

                        console.info(`Request: Retry #${request.tries}`);
                        if (request.tries <= MAX_RETRIES) {
                            return request.retry();
                        }
                    }

                    // If it was not an aborted QIX call, or if we reached MAX_RETRIES, we let the error
                    // trickle down to potential other interceptors, and finally down to resolving/rejecting
                    // the initial promise that the user got when invoking the QIX method:
                    return Promise.reject(error);
                },
            },
        ];
    }

    get appId() {
        return this.config.appId;
    }

    async _connect() {
        console.info('Connecting to qlik web socket...');
        await this.close();
        const url = await this._getSocketUrl();
        try {
            this.session = enigma.create({
                schema,
                url,
                responseInterceptors: this._getResponseInterceptors(),
            });
        } catch (err) {
            throw new Error(`Connect error: ${err.message}`);
        }

        this._addListeners();
        await this.open();
    }

    async open() {
        if (this?.session?.isOpen ?? false) return;
        this.state = SocketState.OPENING_SESSION;
        console.info('Opening session...');
        try {
            this.globalInterface = await this.session?.open();
            this.state = SocketState.OPENED;
        } catch (error) {
            console.error(error);
            throw new Error(
                `Something went wrong opening the session: ${error.message}`
            );
        }
    }

    _addListeners() {
        this.session.on('opened', this._onOpened);
        this.session.on('closed', this._onClosed);
        this.session.on('suspended', this._onSuspended);
        this.session.on('resumed', this._onResumed);
        if (DEBUG) {
            this.session.on('traffic:sent', this._trafficSent);
            this.session.on('traffic:received', this._trafficReceived);
        }
    }

    _trafficSent(req) {
        console.info(req);
    }

    _trafficReceived(res) {
        console.info(res);
    }

    _onOpened() {
        console.info('Session opened');
        this.isOpen = true;
    }

    _onClosed() {
        console.info('Session closed');
        this.isOpen = false;
    }

    _onSuspended({ initiator }) {
        this.suspended = true;
        this.isOpen = false;

        if (initiator === 'network') {
            console.info('Qlik websocket closed due to network');
        }

        if (initiator === 'manual') {
            console.info('Session suspended manually');
        }
    }

    _onResumed() {
        console.info('Session resumed');
        this.suspended = false;
    }

    async close() {
        if (!this?.session?.isOpen ?? false) return;
        this.state = SocketState.CLOSING_SESSION;
        console.info('Closing session...');
        try {
            await this.session.close();
            this.state = SocketState.IDLE;
        } catch (error) {
            console.error(error);
            throw new Error(
                `Something went wrong closing the session: ${error.message}`
            );
        }
    }

    async resume() {
        this.state = SocketState.RESUMING_SESSION;
        console.info('Resuming session...');
        try {
            await this.session?.resume();
            this.state = SocketState.OPENED;
        } catch (error) {
            console.error(error);
            throw new Error(
                `Something went wrong resuming the session: ${error.message}`
            );
        }
    }

    async suspend() {
        this.state = SocketState.SUSPENDING_SESSION;
        console.info('Suspending session...');
        try {
            await this.session?.suspend();
            this.state = SocketState.SUSPENDED;
        } catch (error) {
            console.error(error);
            throw new Error(
                `Something went wrong suspending the session: ${error.message}`
            );
        }
    }

    async getDoc(appId) {
        if (!this?.session?.isOpen ?? false) {
            await this._connect();
        }
        const _appId = appId ?? this.appId;
        return this.globalInterface.openDoc(_appId);
    }

    get socketIsReady() {
        const open = this?.session?.isOpen ?? false;
        const suspended = this?.session?.suspended ?? false;
        if (!open && !suspended && this.state == SocketState.IDLE) {
            return true;
        }
        return false;
    }
}

export default QlikWebSocketManager;
